import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'grammar-progress';

export const useProgress = () => {
  const [completedLessons, setCompletedLessons] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load progress from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const progress = JSON.parse(saved);
        setCompletedLessons(progress.completed || []);
      }
    } catch (error) {
      console.log('No saved progress yet');
    }
    setIsLoaded(true);
  }, []);

  // Save progress to localStorage
  const saveProgress = useCallback((completed) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ completed }));
    } catch (error) {
      console.error('Failed to save progress');
    }
  }, []);

  // Mark a lesson as completed
  const completeLesson = useCallback((lessonId) => {
    setCompletedLessons(prev => {
      if (prev.includes(lessonId)) return prev;
      const newCompleted = [...prev, lessonId];
      saveProgress(newCompleted);
      return newCompleted;
    });
  }, [saveProgress]);

  // Check if a lesson is completed
  const isLessonCompleted = useCallback((lessonId) => {
    return completedLessons.includes(lessonId);
  }, [completedLessons]);

  // Reset all progress
  const resetProgress = useCallback(() => {
    setCompletedLessons([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Get completion percentage
  const getCompletionPercentage = useCallback((totalLessons) => {
    if (totalLessons === 0) return 0;
    return Math.round((completedLessons.length / totalLessons) * 100);
  }, [completedLessons]);

  return {
    completedLessons,
    isLoaded,
    completeLesson,
    isLessonCompleted,
    resetProgress,
    getCompletionPercentage
  };
};

export default useProgress;
